// Every URL inside the app. Routes are English like every other identifier; old URLs (Indonesian ones,
// operator mode under /op) only exist as redirects in app/legacyRoutes.tsx.

type Id = number | string;
type Query = Record<string, string | number>;

const withQuery = (path: string, query?: Query) => {
  const q = query ? new URLSearchParams(Object.entries(query).map(([k, v]) => [k, String(v)])).toString() : '';
  return q ? `${path}?${q}` : path;
};

/** The id of a location page's photo gallery (and the hash that scrolls to it). */
export const LOCATION_PHOTOS_ANCHOR = 'photos';

/** `?monitor=1` on the overview turns monitor (kiosk) mode on, `?monitor=0` off. */
export const MONITOR_PARAM = 'monitor';

/** Path builders for every <Link to>, navigate() and href. */
export const ROUTES = {
  /** the role's home: Beranda (admin, Peninjau), Statistik (Pengawas), operations, deliveries, visits */
  home: '/',
  /** the Ruang Kendali (the map and the live panels) */
  controlRoom: '/control-room',
  /** the overview in monitor (kiosk) mode */
  monitor: withQuery('/control-room', { [MONITOR_PARAM]: 1 }),
  /** Statistik: the aggregates for statistics.read (region and period in the query) */
  statistics: (query?: Query) => withQuery('/statistics', query),
  /** Telusur & tarik: find a set of cylinders, where they are, flag them back */
  trace: '/trace',
  shipments: (query?: Query) => withQuery('/shipments', query),
  shipment: (id: Id) => `/shipments/${id}`,
  shipmentPrint: (id: Id) => `/shipments/${id}/print`,
  /** the public verification page a printed note's QR opens (no sign-in) */
  shipmentVerify: (token: string) => `/v/sj/${encodeURIComponent(token)}`,
  cylinders: (query?: Query) => withQuery('/cylinders', query),
  cylinder: (id: Id) => `/cylinders/${id}`,
  /** the cylinder page with Ganti tag open (an alert about a tag suspected dead) */
  cylinderRetag: (id: Id) => withQuery(`/cylinders/${id}`, { retag: 1 }),
  cylinderByEpc: (epc: string) => `/cylinders/epc/${epc}`,
  locations: '/locations',
  location: (id: Id) => `/locations/${id}`,
  /** the location page, scrolled to its photo gallery */
  locationPhotos: (id: Id) => `/locations/${id}#${LOCATION_PHOTOS_ANCHOR}`,
  gateways: '/gateways',
  gateway: (id: Id) => `/gateways/${id}`,
  alerts: '/alerts',
  /** Peringatan's "Perlu tindakan" tab with filters (severity, province, regency) */
  alertsWhere: (query?: Query) => withQuery('/alerts', query),
  /** Peringatan with one alert's drawer open (the bell, a toast) */
  alert: (id: Id) => withQuery('/alerts', { alert: id }),
  /** Peringatan about one Surat Jalan (its page's "Lihat semua"), optionally with one alert's drawer open */
  shipmentAlerts: (shipmentId: Id, opts: { alert?: Id; state?: string } = {}) =>
    withQuery('/alerts', { shipment: shipmentId, ...(opts.state ? { state: opts.state } : {}), ...(opts.alert == null ? {} : { alert: opts.alert }) }),
  /** Peringatan about one cylinder (its page's "Lihat semua"), optionally with one alert's drawer open */
  cylinderAlerts: (cylinderId: Id, alertId?: Id) => withQuery('/alerts', alertId == null ? { cylinder: cylinderId } : { cylinder: cylinderId, alert: alertId }),
  updates: '/updates',
  users: '/users',
  audit: '/audit',
  /** Log aktivitas filtered to one person */
  auditByUser: (userId: Id) => withQuery('/audit', { user: userId }),
  /** Peringatan filtered to one reader */
  alertsOfReader: (gwId: string) => withQuery('/alerts', { reader: gwId }),
  /** Pembaruan's reader list, searched for one reader */
  updatesOfReader: (gwId: string) => withQuery('/updates', { q: gwId }),
  account: '/account',
  /** the one-time invitation of a new Hub operator (public; the token travels after `#token=`) */
  acceptInvite: '/accept-invite',
  /** Hub registration, public (no sign-in): the landing page, the form and the status check */
  hubRegistration: '/hub-registration',
  hubRegistrationForm: (step?: number) => (step == null ? '/hub-registration/form' : withQuery('/hub-registration/form', { step })),
  hubRegistrationStatus: '/hub-registration/status',
  /** the printable template of the statement that the data are true (signed on duty stamp, then uploaded) */
  hubRegistrationIntegrity: '/hub-registration/integrity-statement',
  /** Pendaftaran Hub (staff) */
  hubApplications: (query?: Query) => withQuery('/hub-applications', query),
  hubApplication: (id: Id) => `/hub-applications/${id}`,
  /** Kunjungan (field verifiers) */
  hubVisits: '/hub-visits',
  hubVisit: (id: Id) => `/hub-visits/${id}`,
  /** Warga (admins): the registered households, one household, the import of the eligible list */
  households: (query?: Query) => withQuery('/households', query),
  household: (id: Id) => `/households/${id}`,
  householdsImport: '/households/import',
  /** Pesanan (admins): the overview by region, status and SLA, one order */
  orders: (query?: Query) => withQuery('/orders', query),
  order: (id: Id) => `/orders/${id}`,
  /** Pengaturan program (national admins) */
  programSettings: '/program-settings',
  /** the print view of cylinder labels (outside the shell, like the Surat Jalan print) */
  cylinderLabels: (ids: readonly Id[]) => withQuery('/cylinders/labels', { ids: ids.join(',') }),
  /** Pengantaran (couriers): today's deliveries and one delivery */
  deliveries: '/deliveries',
  delivery: (id: Id) => `/deliveries/${id}`,
  operations: {
    home: '/operations',
    dispatch: (id?: Id) => (id == null ? '/operations/dispatch' : `/operations/dispatch/${id}`),
    receive: (id?: Id) => (id == null ? '/operations/receive' : `/operations/receive/${id}`),
    register: '/operations/register',
    /** Registrasi with one tag (a read on the home screen) picked: the page reads `epc` */
    registerTag: (epc: string) => withQuery('/operations/register', { epc }),
    lookup: '/operations/lookup',
    /** Catat hasil uji (operators of a Bengkel Uji) */
    test: '/operations/test',
    /** a Hub's operator: Verifikasi Warga (the queue, one household) and Pesanan (the queue, one order) */
    households: '/operations/households',
    household: (id: Id) => `/operations/households/${id}`,
    orders: (query?: Query) => withQuery('/operations/orders', query),
    /** Layanan di loket: walk-in households (no app) */
    counter: '/operations/counter',
    /** a Hub's couriers: their recap over a period and the day's cash handed over */
    couriers: (query?: Query) => withQuery('/operations/couriers', query),
    /** Stock opname: count the Hub's cylinders and compare with what is recorded */
    stockCount: '/operations/stock-count',
    order: (id: Id) => `/operations/orders/${id}`,
  },
} as const;

/** Full URL of an in-app path, for QR codes and shared links. */
export const absoluteUrl = (path: string) => `${window.location.origin}${path}`;

/** <Route path> patterns: top level, inside the laptop shell (relative), inside operations mode (relative). */
export const PATTERNS = {
  shipmentPrint: '/shipments/:id/print',
  cylinderLabels: '/cylinders/labels',
  /** public, outside the session (app/Root.tsx) */
  shipmentVerify: '/v/sj/:token',
  /** public, outside the session (app/Root.tsx) */
  acceptInvite: '/accept-invite',
  /** public, outside the session (app/Root.tsx): every page under it */
  hubRegistrationRoot: '/hub-registration/*',
  hubRegistration: {
    form: 'form',
    status: 'status',
    integrity: 'integrity-statement',
  },
  operationsRoot: '/operations',
  shell: {
    controlRoom: 'control-room',
    statistics: 'statistics',
    trace: 'trace',
    shipments: 'shipments',
    shipment: 'shipments/:id',
    cylinders: 'cylinders',
    cylinderByEpc: 'cylinders/epc/:epc',
    cylinder: 'cylinders/:id',
    locations: 'locations',
    location: 'locations/:id',
    gateways: 'gateways',
    gateway: 'gateways/:id',
    alerts: 'alerts',
    account: 'account',
    updates: 'updates',
    users: 'users',
    audit: 'audit',
    hubApplications: 'hub-applications',
    hubApplication: 'hub-applications/:id',
    hubVisits: 'hub-visits',
    hubVisit: 'hub-visits/:id',
    households: 'households',
    householdsImport: 'households/import',
    household: 'households/:id',
    orders: 'orders',
    order: 'orders/:id',
    programSettings: 'program-settings',
    deliveries: 'deliveries',
    delivery: 'deliveries/:id',
  },
  operations: {
    dispatch: 'dispatch',
    dispatchShipment: 'dispatch/:id',
    receive: 'receive',
    receiveShipment: 'receive/:id',
    register: 'register',
    lookup: 'lookup',
    test: 'test',
    households: 'households',
    household: 'households/:id',
    orders: 'orders',
    order: 'orders/:id',
    counter: 'counter',
    couriers: 'couriers',
    stockCount: 'stock-count',
    location: 'location',
  },
} as const;
